import React, { useMemo } from 'react';

import classNames from 'classnames';

import TriangleDown from 'Assets/icons/triangle-down.svg';
import TriangleUp from 'Assets/icons/triangle-up.svg';

const DIRECTION_UP = 'UP';
const DIRECTION_DOWN = 'DOWN';

const SIGN_POSITIVE = 'positive';
const SIGN_NEGATIVE = 'negative';
const SIGN_NEUTRAL = 'neutral';

/**
 * @param {'primary'|'secondary'} [size]   'secondary' → compact padding, smaller value, faint fill.
 * @param {boolean} [signed]               Apply a green/red tint by the sign of `signValue`.
 * @param {number}  [signValue]            The numeric value whose sign drives the signed treatment.
 * @param {string}  [valueTitle]           Hover tooltip (full value) on the value.
 * @param {node}    [subtitle]             A muted line under the value.
 *
 * With all new props at their defaults the render is identical to the original tile.
 */
export default function KeyStatTile({
    title,
    value,
    percentage,
    percentageLabel,
    reversed,
    size = 'primary',
    signed = false,
    signValue = 0,
    valueTitle,
    subtitle,
}) {
    const diraction = useMemo(() => (percentage * (reversed ? -1 : 1) < 0 ? DIRECTION_DOWN : DIRECTION_UP), [percentage]);
    const colorClass = useMemo(() => (percentage < 0 ? 'tw-text-red' : 'tw-text-green'), [percentage, reversed]);

    const isSecondary = size === 'secondary';
    const sign = signed ? (Number(signValue) > 0 ? SIGN_POSITIVE : Number(signValue) < 0 ? SIGN_NEGATIVE : SIGN_NEUTRAL) : SIGN_NEUTRAL;

    const containerClass = classNames('tw-rounded-lg tw-border-2', isSecondary ? 'tw-px-3 tw-py-2' : 'tw-px-4 tw-py-3', {
        'tw-border-grey': sign === SIGN_NEUTRAL && !isSecondary,
        'tw-border-grey tw-bg-charcoal-10': sign === SIGN_NEUTRAL && isSecondary,
        'tw-bg-green-10 tw-border-green-25': sign === SIGN_POSITIVE,
        'tw-bg-red-10 tw-border-red-25': sign === SIGN_NEGATIVE,
    });

    const labelClass = classNames('tw-font-medium mb-2', isSecondary ? 'tw-text-xs' : 'tw-text-sm', {
        'tw-text-blue-75': sign === SIGN_NEUTRAL,
        'tw-text-green': sign === SIGN_POSITIVE,
        'tw-text-red': sign === SIGN_NEGATIVE,
    });

    const valueClass = classNames('tw-font-semibold', isSecondary ? 'tw-text-xl' : 'tw-text-2.5xl', {
        'tw-text-charcoal': sign === SIGN_NEUTRAL,
        'tw-text-green': sign === SIGN_POSITIVE,
        'tw-text-red': sign === SIGN_NEGATIVE,
    });

    return (
        <div className={containerClass}>
            <div>
                <div className={labelClass}>{title}</div>
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    <div className={valueClass} title={valueTitle}>
                        {value}
                    </div>
                    {typeof percentage === 'number' && (
                        <div className={`tw-flex tw-items-center tw-gap-x-1 ${colorClass}`}>
                            {diraction === DIRECTION_DOWN ? <TriangleDown className="tw-w-4" /> : <TriangleUp className="tw-w-4" />}
                            <div className="tw-text-xs">{percentageLabel != null ? percentageLabel : percentage}</div>
                        </div>
                    )}
                </div>
                {subtitle != null && subtitle !== '' && <div className="tw-text-xs tw-text-charcoal-50 tw-mt-1">{subtitle}</div>}
            </div>
        </div>
    );
}
